import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Grid,
    Chip,
    LinearProgress,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper
} from '@mui/material';
import {
    Add as AddIcon,
    Work as WorkIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { getWorksListAWS, deleteWorkAWS } from '../../offchain-logic/services';
import { updateWorkModern } from '../../services/authService';
import moment from 'moment';

interface Work {
    id_work: string;
    work_name: string;
    id_activity: string;
    id_model: string;
    work_date: string;
    description: string;
    status: string;
    completion_count: number;
    total_required: number;
    completion_percentage: number;
    creation_date: string;
    updated_date: string;
}

interface Props {
    handleOpenToast: (message: string, severity: 'success' | 'error') => void;
}

export const WorksList: React.FC<Props> = ({ handleOpenToast }) => {
    const navigate = useNavigate();
    
    const [works, setWorks] = useState<Work[]>([]);
    const [selectedActivity, setSelectedActivity] = useState<string>('');
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [workToDelete, setWorkToDelete] = useState<Work | null>(null);
    
    // Verifica se l'utente è admin
    const userData = JSON.parse(sessionStorage.getItem('user') ?? "{}");
    const userRole = userData.role || '';
    const isAdmin = userRole === 'Admin';

    const loadWorks = (activityId: string) => {
        if (!activityId) return;
        
        getWorksListAWS(activityId, (err: any, data: any) => {
            if (err) {
                console.error('[WORKS] Error:', err);
                handleOpenToast('Errore caricamento pratiche', 'error');
                return;
            }
            
            if (data?.works) {
                // Ordina per data di creazione decrescente (più recenti in alto)
                const sortedWorks = [...data.works].sort((a, b) => {
                    return new Date(b.creation_date).getTime() - new Date(a.creation_date).getTime();
                });
                setWorks(sortedWorks);
            }
        });
    };

    useEffect(() => {
        // Ascolta l'evento activityChanged dall'Header
        const handleActivityChanged = (event: any) => {
            const activityId = event.detail;
            if (activityId) {
                setSelectedActivity(activityId);
                loadWorks(activityId);
            }
        };

        window.addEventListener('activityChanged', handleActivityChanged);

        // Prova a caricare subito se l'activity è già in sessionStorage (es: refresh pagina)
        const activityId = sessionStorage.getItem('selectedActivityId');
        if (activityId) {
            setSelectedActivity(activityId);
            loadWorks(activityId);
        }

        return () => {
            window.removeEventListener('activityChanged', handleActivityChanged);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDeleteClick = (work: Work) => {
        setWorkToDelete(work);
        setOpenDeleteDialog(true);
    };

    const handleDeleteConfirm = () => {
        if (!workToDelete) return;
        
        deleteWorkAWS(workToDelete.id_work, (err: any, data: any) => {
            setOpenDeleteDialog(false);
            
            if (err) {
                console.error('[DELETE WORK] Error:', err);
                handleOpenToast('Errore eliminazione lavoro. Verifica che non ci siano documenti caricati.', 'error');
                return;
            }
            
            handleOpenToast('Pratica eliminata con successo', 'success');
            loadWorks(selectedActivity);
        });
    };
    
    const handleApproveWork = async (workId: string) => {
        try {
            await updateWorkModern(workId, { status: 'completed' });
            handleOpenToast('Pratica approvata con successo', 'success');
            loadWorks(selectedActivity);
        } catch (error) {
            console.error('[APPROVE] Error:', error);
            handleOpenToast('Errore approvazione pratica', 'error');
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed': return 'success';
            case 'in_progress': return 'primary';
            case 'pending': return 'warning';
            case 'cancelled': return 'error';
            default: return 'default';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'completed': return 'Completato';
            case 'in_progress': return 'In Corso';
            case 'pending': return 'In Attesa';
            case 'cancelled': return 'Annullato';
            default: return status;
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: '#8B6F47' }}>
                        <WorkIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#8B6F47' }} />
                        Gestione Pratiche
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Gestisci le pratiche e i documenti per ogni attività
                    </Typography>
                </Box>
                
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/create-work')}
                    sx={{
                        bgcolor: '#20679F',
                        '&:hover': { bgcolor: '#1a5280' }
                    }}
                >
                    Nuova Pratica
                </Button>
            </Box>

            {/* Works Grid */}
            {!selectedActivity ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <WorkIcon sx={{ fontSize: 80, color: '#ccc', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        Nessuna attività selezionata
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Seleziona un'attività dall'header per visualizzare le pratiche
                    </Typography>
                </Box>
            ) : works.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <WorkIcon sx={{ fontSize: 80, color: '#ccc', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        Nessuna pratica presente
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Crea la tua prima pratica per questa attività
                    </Typography>
                </Box>
            ) : (
                <>
                    {/* Ultime 3 pratiche in card */}
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                        Ultime Pratiche
                    </Typography>
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        {works.slice(0, 3).map((work) => (
                        <Grid item xs={12} md={6} lg={4} key={work.id_work}>
                            <Card 
                                sx={{ 
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    '&:hover': {
                                        boxShadow: 6,
                                        transform: 'translateY(-4px)',
                                        transition: 'all 0.3s'
                                    }
                                }}
                            >
                                <CardContent sx={{ flexGrow: 1 }}>
                                    {/* Header */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                                        <Box sx={{ flexGrow: 1 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                                {work.work_name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Creato: {moment(work.creation_date).format('DD/MM/YYYY')}
                                            </Typography>
                                        </Box>
                                        <Chip 
                                            label={getStatusLabel(work.status)}
                                            color={getStatusColor(work.status)}
                                            size="small"
                                        />
                                    </Box>

                                    {/* Description */}
                                    {work.description && (
                                        <Typography 
                                            variant="body2" 
                                            color="text.secondary" 
                                            sx={{ mb: 2, minHeight: 40 }}
                                        >
                                            {work.description.length > 100 
                                                ? work.description.substring(0, 100) + '...' 
                                                : work.description}
                                        </Typography>
                                    )}

                                    {/* Progress */}
                                    <Box sx={{ mt: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Completamento
                                            </Typography>
                                            <Typography variant="body2" fontWeight="bold">
                                                {work.completion_percentage}% ({work.completion_count}/{work.total_required})
                                            </Typography>
                                        </Box>
                                        <LinearProgress 
                                            variant="determinate" 
                                            value={work.completion_percentage}
                                            sx={{ 
                                                height: 8, 
                                                borderRadius: 4,
                                                bgcolor: '#e0e0e0',
                                                '& .MuiLinearProgress-bar': {
                                                    bgcolor: work.completion_percentage === 100 ? '#4caf50' : '#1976d2'
                                                }
                                            }}
                                        />
                                    </Box>

                                    {/* Actions */}
                                    <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<ViewIcon />}
                                            onClick={() => navigate(`/work-detail/${work.id_work}`)}
                                            fullWidth
                                        >
                                            Apri
                                        </Button>
                                        
                                        {/* Pulsante Approva per Admin solo se status = pending E documenti al 100% */}
                                        {isAdmin && work.status === 'pending' && work.completion_percentage === 100 && (
                                            <IconButton
                                                size="small"
                                                onClick={() => handleApproveWork(work.id_work)}
                                                sx={{ 
                                                    color: 'success.main',
                                                    '&:hover': { bgcolor: 'success.light', color: 'white' }
                                                }}
                                                title="Approva e Completa"
                                            >
                                                <CheckCircleIcon />
                                            </IconButton>
                                        )}
                                        
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDeleteClick(work)}
                                            disabled={work.status === 'pending' || work.status === 'completed'}
                                            sx={{ 
                                                color: 'error.main',
                                                '&:hover': { bgcolor: 'error.light', color: 'white' },
                                                '&.Mui-disabled': { color: 'rgba(0, 0, 0, 0.26)' }
                                            }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
                
                {/* Tabella con tutte le pratiche */}
                <Typography variant="h6" sx={{ mt: 5, mb: 2, fontWeight: 'bold' }}>
                    Tutte le Pratiche
                </Typography>
                <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
                    <Table>
                        <TableHead sx={{ bgcolor: '#20679F' }}>
                            <TableRow>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Nome Pratica</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Stato</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Data Creazione</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Data Pratica</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Completamento</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Descrizione</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>Azioni</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {works.map((work) => (
                                <TableRow 
                                    key={work.id_work}
                                    sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}
                                >
                                    <TableCell sx={{ fontWeight: 'bold' }}>{work.work_name}</TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={getStatusLabel(work.status)}
                                            color={getStatusColor(work.status)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>{moment(work.creation_date).format('DD/MM/YYYY HH:mm')}</TableCell>
                                    <TableCell>{moment(work.work_date).format('DD/MM/YYYY')}</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <LinearProgress 
                                                variant="determinate" 
                                                value={work.completion_percentage}
                                                sx={{ 
                                                    flex: 1,
                                                    height: 8, 
                                                    borderRadius: 4,
                                                    '& .MuiLinearProgress-bar': {
                                                        bgcolor: work.completion_percentage === 100 ? '#4caf50' : '#1976d2'
                                                    }
                                                }}
                                            />
                                            <Typography variant="caption" sx={{ minWidth: 45 }}>
                                                {work.completion_percentage}%
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 200 }}>
                                        <Typography variant="body2" noWrap>
                                            {work.description || '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                            <IconButton
                                                size="small"
                                                onClick={() => navigate(`/work-detail/${work.id_work}`)}
                                                color="primary"
                                                title="Apri"
                                            >
                                                <ViewIcon />
                                            </IconButton>
                                            
                                            {isAdmin && work.status === 'pending' && work.completion_percentage === 100 && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleApproveWork(work.id_work)}
                                                    sx={{ color: 'success.main' }}
                                                    title="Approva e Completa"
                                                >
                                                    <CheckCircleIcon />
                                                </IconButton>
                                            )}
                                            
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDeleteClick(work)}
                                                disabled={work.status === 'pending' || work.status === 'completed'}
                                                color="error"
                                                title="Elimina"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                </>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
                <DialogTitle>Conferma Eliminazione</DialogTitle>
                <DialogContent>
                    <Typography>
                        Sei sicuro di voler eliminare la pratica "<strong>{workToDelete?.work_name}</strong>"?
                    </Typography>
                    <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                        Attenzione: L'eliminazione è possibile solo se non ci sono documenti caricati.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)}>Annulla</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        Elimina
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
